import { Dropdown, Tooltip } from "antd";
import { clsx } from "clsx";
import {
  cloneElement,
  forwardRef,
  isValidElement,
  ReactElement,
  ReactNode,
  RefObject,
  useState,
} from "react";
import { Icon, type IconName } from "../Icons";
import { LoadingSpinner } from "../LoadingSpinner";
import "./DropdownButton.css";

/**
 * Option interface for dropdown menu items
 * @interface DropdownButtonOption
 * @property {React.ReactNode} label - Text label displayed in the dropdown menu
 * @property {() => Promise<void> | void} onClick - Action to perform when clicked
 * @property {(error: unknown) => void} [onError] - Optional error handler
 */
export interface DropdownButtonOption {
  label: ReactNode;
  onClick: () => Promise<void> | void;
  onError?: (error: unknown) => void;
  disabled?: boolean;
  tooltip?: string;
  icon?: ReactNode;
}

/**
 * Props for the DropdownButton component
 * @interface DropdownButtonProps
 * @property {DropdownButtonOption[]} options - Array of options for the dropdown menu
 * @property {string} label - Text label for the main button
 * @property {"primary" | "secondary" | "success" | "danger" | "ghost"} [variant="primary"] - Visual style variant
 * @property {"xs" | "sm" | "smd" | "md" | "lg" | "xl"} [size="md"] - Size variant
 * @property {boolean} [disabled=false] - Whether the button is disabled
 * @property {string} [iconName] - Optional icon name for the button label
 * @property {React.ReactNode} [icon] - Optional React node for custom icon
 * @property {string} [className] - Optional additional CSS class
 */
export interface DropdownButtonProps {
  options: DropdownButtonOption[];
  label: ReactNode;
  variant?: "primary" | "secondary" | "success" | "danger" | "ghost";
  size?: "xs" | "sm" | "smd" | "md" | "lg" | "xl";
  disabled?: boolean;
  iconName?: IconName;
  icon?: React.ReactNode;
  className?: string;
  onPrimaryAction?: () => Promise<void> | void;
  primaryDisabled?: boolean;
  primaryLoading?: boolean;
  onPrimaryError?: (error: unknown) => void;
  /**
   * When no `onPrimaryAction` fires, clicking the primary button opens the
   * dropdown menu instead of doing nothing. Lets the primary stay clickable
   * (not grayed out) and fall back to the option list.
   */
  primaryClickOpensMenu?: boolean;
  /** Override accessibility label. Required when `label` is a ReactNode (not a string). */
  ariaLabel?: string;
  /** Collapse label until hover/focus */
  collapseToIcon?: boolean;
  /**
   * Custom dropdown panel content. When provided, the dropdown renders this
   * arbitrary node (e.g. a `Checkbox` + `RadioGroup` settings panel) via
   * antd's `dropdownRender` instead of the `options` list — `options` may be
   * passed as `[]` in this mode. Use for split buttons whose menu is a small
   * form rather than a list of actions.
   */
  dropdownContent?: ReactNode;
}

/**
 * DropdownButton component
 *
 * A button that displays a dropdown menu when clicked
 * Supports loading states, icons, and various styling options
 * Available variants: primary, secondary, success, danger, ghost
 * Available sizes: xs (22px), sm (22px), smd (24px), md (28px), lg (36px), xl (44px)
 *
 * @example
 * ```tsx
 * <DropdownButton
 *   label="Actions"
 *   variant="ghost"
 *   size="md"
 *   iconName="settings"
 *   options={[
 *     { label: "Edit", onClick: handleEdit },
 *     { label: "Delete", onClick: handleDelete }
 *   ]}
 * />
 * ```
 */
export const DropdownButton = forwardRef<HTMLElement, DropdownButtonProps>(
  (
    {
      options,
      label,
      variant = "primary",
      size = "md",
      disabled = false,
      iconName,
      icon,
      className = "",
      onPrimaryAction,
      primaryDisabled = false,
      primaryLoading = false,
      onPrimaryError,
      ariaLabel,
      collapseToIcon = false,
      primaryClickOpensMenu = false,
      dropdownContent,
    },
    ref,
  ) => {
    const [isLoading, setIsLoading] = useState(false);
    const [open, setOpen] = useState(false);

    /**
     * Handles click events on dropdown menu items
     * Manages loading state and error handling
     */
    const handleClick = async (
      onClick: () => Promise<void> | void,
      onError?: (error: unknown) => void,
    ) => {
      setIsLoading(true);
      try {
        await onClick();
      } catch (error) {
        if (onError) {
          onError(error);
        } else {
          console.error("Action failed:", error);
        }
      } finally {
        setIsLoading(false);
      }
    };

    // Configure dropdown menu items
    const items = options.map((option: DropdownButtonOption, index: number) => {
      const content = option.icon ? (
        // biome-ignore lint/correctness/useJsxKeyInIterable: map returns antd menu-item objects (keyed via `key`), not React list elements
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {option.icon}
          {option.label}
        </span>
      ) : (
        option.label
      );

      return {
        key: index.toString(),
        label: option.tooltip ? (
          <Tooltip title={option.tooltip}>{content}</Tooltip>
        ) : (
          content
        ),
        disabled: option.disabled,
        onClick: () => handleClick(option.onClick, option.onError),
      };
    });

    // Map component size to Ant Design size
    const buttonSize =
      size === "xs"
        ? "small"
        : size === "sm"
          ? "small"
          : size === "smd"
            ? "small"
            : size === "lg"
              ? "large"
              : size === "xl"
                ? "large" // Map xl to Ant's large size and apply custom height via CSS
                : "middle";

    const dropdownIconSize = size === "xs" ? 8 : 10;

    // Custom dropdown icon with design system styles
    const dropdownIcon = isLoading ? (
      <div className="indigo-dropdown-loading-spinner">
        <LoadingSpinner size={16} />
      </div>
    ) : (
      <svg
        width={dropdownIconSize}
        height={dropdownIconSize}
        viewBox="0 0 10 10"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="indigo-dropdown-icon"
      >
        <path d="M1 1.5L5 5.5L9 1.5" stroke="currentColor" strokeWidth="2" />
      </svg>
    );

    // Use clsx to generate component className
    const componentClassName = clsx(
      "indigo-dropdown-button",
      `indigo-dropdown-${variant}`,
      `indigo-dropdown-${size}`,
      className,
      collapseToIcon && "indigo-dropdown-collapsible",
    );

    const shouldCustomizePrimaryButton = primaryDisabled || primaryLoading;

    const accessibleLabel =
      ariaLabel ||
      (collapseToIcon && typeof label === "string" ? label : undefined);

    const renderButtons = (buttons: ReactNode[]): ReactNode[] => {
      if (
        !shouldCustomizePrimaryButton ||
        buttons.length < 2 ||
        !isValidElement(buttons[0])
      ) {
        return buttons;
      }

      const [primaryButton, dropdownButton] = buttons as [
        ReactElement,
        ReactNode,
      ];

      const customizedPrimary = cloneElement(primaryButton, {
        disabled: primaryDisabled,
        loading: primaryLoading,
      });

      return [customizedPrimary, dropdownButton];
    };

    const handlePrimaryButtonClick = async () => {
      if (!onPrimaryAction) {
        // No direct action: fall back to opening the menu so the primary
        // button stays clickable instead of being a dead click zone.
        if (primaryClickOpensMenu) {
          setOpen(true);
        }
        return;
      }
      try {
        await onPrimaryAction();
      } catch (error) {
        if (onPrimaryError) {
          onPrimaryError(error);
        } else {
          console.error("Primary action failed:", error);
        }
      }
    };

    return (
      <div
        className={componentClassName}
        ref={ref as RefObject<HTMLDivElement>}
      >
        <Dropdown.Button
          type="primary"
          size={buttonSize}
          disabled={isLoading || disabled}
          menu={{ items }}
          dropdownRender={
            dropdownContent
              ? () => (
                  <div className="indigo-dropdown-panel">{dropdownContent}</div>
                )
              : undefined
          }
          open={open}
          onOpenChange={setOpen}
          overlayClassName="indigo-dropdown-menu-overlay"
          className={clsx(
            "indigo-dropdown-button-inner",
            `indigo-dropdown-variant-${variant}`,
          )}
          icon={dropdownIcon}
          buttonsRender={
            shouldCustomizePrimaryButton ? renderButtons : undefined
          }
          onClick={
            onPrimaryAction || primaryClickOpensMenu
              ? handlePrimaryButtonClick
              : undefined
          }
          aria-label={accessibleLabel}
        >
          <span className="indigo-dropdown-button-content">
            {/*
              While the primary action is loading, antd prepends its own
              spinner to these children rather than swapping the icon out. So
              the static icon has to stand down, or the two stack up in the
              same slot — which is what they did, most visibly in the narrow
              sticky action bar. Matches Button, where `loading` replaces the
              icon outright.
            */}
            {iconName && !primaryLoading && (
              <Icon name={iconName} className="indigo-dropdown-button-icon" />
            )}
            {icon && !iconName && !primaryLoading && (
              <span className="indigo-dropdown-button-icon">{icon}</span>
            )}
            <span className="indigo-dropdown-button-label">{label}</span>
          </span>
        </Dropdown.Button>
      </div>
    );
  },
);

DropdownButton.displayName = "DropdownButton";
